import {
  AccessTokenResponse,
  createAxiosWithAuth,
  TokenError,
} from '@dtvgo/axios-interceptor-retry-auth';
import {
  createToolboxTvService,
  shouldUpdateToken,
  getErrorCode,
} from '@dtvgo/toolbox-service';

import { deviceId } from '~/store/deviceId';
import { CLIENT_HEADERS_OPTIONS } from '~/utils/clientHeadersOptions';
import { TOOLBOX_REGION } from '~/utils/constants';

import {
  createCredentialsFromToolboxResponse,
  decodeToolboxToken,
} from '~/utils/toolbox';
import { errorFactory } from '~/utils/errorFactory';
import { ErrorCode, Source } from '@dtvgo/error';
import { areEqualArrays, compareStrings } from '~/utils/functions';

import { stopPlayback } from '~/cast/lib';
import {
  authError,
  credentials,
  decodedIdToken,
  setAuthError,
  setCredentials,
  userId,
} from './data';
import { loadCredentials } from './utils';

const get = () => {
  const creds = loadCredentials();
  return {
    access_token: creds?.accessToken || '',
    refresh_token: creds?.refreshToken || '',
    id_token: creds?.idToken || '',
    scope: creds?.scope || '',
    token_type: creds?.tokenType || 'Bearer',
    expires_in: creds?.expiresIn || 0,
  };
};

const set = (data: AccessTokenResponse) => {
  setAuthError(undefined);
  setCredentials(createCredentialsFromToolboxResponse(data));
};

const fail = (error: TokenError) => {
  if (authError()) return;
  const vrioError = errorFactory.create('Toolbox tokens error', {
    source: Source.AUTHENTICATION,
    code: getErrorCode(error),
  });
  setCredentials(undefined);
  setAuthError(vrioError);
  stopPlayback();
};

export const tokens = { get, set, fail, version: 'v2' };

export const toolbox = createToolboxTvService({
  host: import.meta.env.VITE_TOOLBOX_HOST,
  deviceId: deviceId(),
  clientHeadersOptions: CLIENT_HEADERS_OPTIONS,
  region: TOOLBOX_REGION,
  tokens,
  authorization: import.meta.env.VITE_TOOLBOX_OPEN_AM_KEY,
});

export const authInterceptor = createAxiosWithAuth(
  toolbox.axiosInstance,
  tokens,
  () => TOOLBOX_REGION,
);

let updatePromise: Promise<void> | undefined;

export async function updateAuthTokens(): Promise<void> {
  if (updatePromise) {
    return updatePromise;
  }

  const { refreshToken } = credentials() || {};
  const { businessUnit, msoProvider } = decodedIdToken() || {};

  if (!refreshToken || !businessUnit) {
    return undefined;
  }

  updatePromise = (async () => {
    try {
      await toolbox.v2.tv.refreshToken(refreshToken, {
        businessUnit,
        msoProvider,
      });
    } catch (error) {
      console.error('Error updating auth tokens:', error);
    } finally {
      updatePromise = undefined;
    }
  })();

  return updatePromise;
}

export async function tokensAreUsable(): Promise<boolean> {
  const offsetMinutes =
    Number(import.meta.env.VITE_MINUTES_BEFORE_TOKEN_REFRESH) || 240;
  const creds = loadCredentials();
  if (!creds?.refreshToken || !creds?.idToken) {
    return false;
  }

  const { isIdTokenExpired, isIdTokenExpiring, isRefreshTokenExpired } =
    shouldUpdateToken({
      refreshToken: creds.refreshToken,
      idToken: creds.idToken,
      offsetMinutes,
    });

  if (isRefreshTokenExpired) {
    return false;
  }

  if (isIdTokenExpiring && !isIdTokenExpired) {
    await updateAuthTokens();
  }

  return true;
}

export async function createInitialAuthTokens(idTokenFromSender: string) {
  // Generate the device and user codes
  const {
    data: { user_code: userCode, device_code: deviceCode },
  } = await toolbox.v2.tv.getDeviceCode();

  // Activate the device with the user code
  await toolbox.v2.tv.activateDevice(userCode, idTokenFromSender);

  // Login the device with the device code
  const { data } = await toolbox.v2.tv.loginDevice(deviceCode);

  if (!data || !data.access_token || !data.refresh_token || !data.id_token) {
    throw errorFactory.create('Toolbox tokens error', {
      source: Source.AUTHENTICATION,
      code: ErrorCode.UNEXPECTED_ERROR,
    });
  }

  return createCredentialsFromToolboxResponse(data);
}

export async function login(idTokenFromSender: string) {
  const webTokenData = decodeToolboxToken(idTokenFromSender);
  const currentUserId = userId();
  const isSameUser =
    webTokenData?.vrioCustomerId &&
    currentUserId &&
    webTokenData.vrioCustomerId === currentUserId;

  if (isSameUser) {
    const decoded = decodedIdToken();
    const channels = decoded?.channels ?? [];

    const hasChannels = channels.length > 0;
    const hasNewChannels = !areEqualArrays(
      channels,
      webTokenData.channels,
      compareStrings,
    );

    const shouldSkip = hasChannels && !hasNewChannels;
    const usable = await tokensAreUsable();
    if (shouldSkip || usable) {
      return;
    }
  }

  // Full first‐time flow
  const newCreds = await createInitialAuthTokens(idTokenFromSender);
  setCredentials(newCreds);
  setAuthError(undefined);
}
